"use client";

import { useRef, useEffect } from "react";

/**
 * Creates DOM element to be used as React root.
 */
function createRootElement(id: string) {
  const rootContainer = document.createElement("div");
  rootContainer.setAttribute("id", id);
  return rootContainer;
}

/**
 * Appends element as last child of body.
 */
function addRootElement(rootElement: Node) {
  document.body.insertBefore(
    rootElement,
    (document.body.lastElementChild as any).nextElementSibling,
  );
}

/**
 * Hook to create a React Portal.
 * Automatically handles creating and tearing-down the root elements (no SRR
 * makes this trivial), so there is no need to ensure the parent target already
 * exists.
 * @example
 * const target = usePortal(id, [id]);
 * return createPortal(children, target);
 */
function usePortal(id: string, skipElementCreation?: boolean): Element {
  const rootElementReference = useRef<Element | null>(null);

  useEffect(
    function setupElement() {
      // Look for existing target dom element to append to
      const existingParent = document.querySelector(`#${id}`);
      // Parent is either a new root or the existing dom element
      const parentElement =
        existingParent || (!skipElementCreation && createRootElement(id));

      // If there is no existing DOM element, add a new one.
      if (!existingParent && parentElement) {
        addRootElement(parentElement);
      }

      // Add the detached element to the parent
      if (rootElementReference.current && parentElement) {
        parentElement.append(rootElementReference.current);
      }

      return function removeElement() {
        if (rootElementReference.current) {
          (rootElementReference.current as any).remove();
        }
        if (
          parentElement &&
          !parentElement.childElementCount &&
          !skipElementCreation
        ) {
          parentElement.remove();
        }
      };
    },
    [id],
  );

  /**
   * It's important we evaluate this lazily:
   * - We need first render to contain the DOM element, so it shouldn't happen
   *   in useEffect. We would normally put this in the constructor().
   * - We can't do 'const rootElemRef = useRef(document.createElement('div))',
   *   since this will run every single render (that's a lot).
   * - We want the ref to consistently point to the same DOM element and only
   *   ever run once.
   * @link https://reactjs.org/docs/hooks-faq.html#how-to-create-expensive-objects-lazily
   */
  function getRootElement() {
    if (!rootElementReference.current) {
      rootElementReference.current = document.createElement("div") as Element;
    }
    return rootElementReference.current;
  }

  return getRootElement();
}

export default usePortal;
